// Network logging to monitor and allowlist API calls
export interface NetworkLogEntry {
  timestamp: number;
  method: string;
  url: string;
  host: string;
  allowed: boolean;
  blocked?: boolean;
}

export class NetworkLogger {
  private static instance: NetworkLogger;
  private logs: NetworkLogEntry[] = [];
  private allowedHosts = new Set([
    'api.dropboxapi.com',
    'content.dropboxapi.com'
  ]);

  private constructor() {
    this.initializeInterceptor();
  }

  static getInstance(): NetworkLogger {
    if (!NetworkLogger.instance) {
      NetworkLogger.instance = new NetworkLogger();
    }
    return NetworkLogger.instance;
  }

  private initializeInterceptor(): void {
    // Intercept fetch calls
    const originalFetch = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input.toString();
      const method = init?.method || 'GET';
      
      this.logRequest(method, url);
      
      // Check if host is allowed
      if (!this.isHostAllowed(url)) {
        console.warn(`Blocked network request to disallowed host: ${url}`);
        throw new Error(`Network request blocked: ${url}`);
      }
      
      return originalFetch(input, init);
    };

    // Intercept XMLHttpRequest (if any components still use it)
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, async?: boolean, username?: string | null, password?: string | null) {
      const urlString = url.toString();
      NetworkLogger.getInstance().logRequest(method, urlString);
      
      if (!NetworkLogger.getInstance().isHostAllowed(urlString)) {
        console.warn(`Blocked XMLHttpRequest to disallowed host: ${urlString}`);
        throw new Error(`XMLHttpRequest blocked: ${urlString}`);
      }
      
      return originalXHROpen.call(this, method, url, async || true, username, password);
    };
  }

  private isHostAllowed(url: string): boolean {
    try {
      const urlObj = new URL(url);
      
      // Allow relative URLs (local resources)
      if (!urlObj.hostname) return true;
      
      // Allow localhost and local development
      if (urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1') {
        return true;
      }
      
      // Check against allowlist
      return this.allowedHosts.has(urlObj.hostname);
    } catch (error) {
      // If URL parsing fails, it's probably a relative URL
      return true;
    }
  }

  private logRequest(method: string, url: string): void {
    const timestamp = Date.now();
    const host = this.extractHost(url);
    const allowed = this.isHostAllowed(url);
    
    const entry: NetworkLogEntry = {
      timestamp,
      method,
      url,
      host,
      allowed,
      blocked: !allowed
    };
    
    this.logs.push(entry);
    
    // Keep only last 100 entries
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }
  }

  private extractHost(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname || 'localhost';
    } catch (error) {
      return 'localhost';
    }
  }

  // Public methods
  getLogs(): NetworkLogEntry[] {
    return [...this.logs].reverse(); // Most recent first
  }

  clearLogs(): void {
    this.logs = [];
  }

  addAllowedHost(host: string): void {
    this.allowedHosts.add(host);
  }

  removeAllowedHost(host: string): void {
    this.allowedHosts.delete(host);
  }

  getAllowedHosts(): string[] {
    return Array.from(this.allowedHosts);
  }
}

// Initialize singleton
export const networkLogger = NetworkLogger.getInstance();